import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from, Observable } from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter, matchRoutes } from "react-router-dom";
import { initializeFaro, createReactRouterV6DataOptions, ReactIntegration, getWebInstrumentations } from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import "./style/global.css";
import {config} from './config/config'
import {Amplify} from 'aws-amplify'
import {fetchAuthSession} from 'aws-amplify/auth'
import AppProvider from "./Provider/AppProvider";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration"
import Helmet from "react-helmet";
import { roles } from "./helper";
import "@fontsource/poppins";
import {
  getHasuraClaimsFromToken,
  getStoredIdToken,
  isStoredTokenExpired,
  isStoredTokenIssuedInFuture,
} from "./authToken";


Amplify.configure(config.amplifyConfig);

const faroCollectorUrl = (process.env.REACT_APP_FARO_URL || "").trim();
if (faroCollectorUrl) {
  initializeFaro({
    url: faroCollectorUrl,
    app: {
      name: process.env.REACT_APP_FARO_APP_NAME,
      version: process.env.REACT_APP_FARO_APP_VERSION,
      environment: process.env.REACT_APP_FARO_ENV,
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation(),
      new ReactIntegration({
        router: createReactRouterV6DataOptions({ matchRoutes }),
      }),
    ],
  });
}

const container = document.getElementById("root");
const root = createRoot(container!);

const getNewAccessToken = async () => {
  return fetchAuthSession({forceRefresh: true})
};

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  const isJwtError = graphQLErrors?.some(
    e => e.message?.includes('JWTExpired') || e.message?.includes('JWTIssuedAtFuture')
  );
  const isAllowlistError = graphQLErrors?.some(
    (e) =>
      e.message?.toLowerCase().includes("query is not allowed") ||
      e.message?.toLowerCase().includes("allowlist")
  );

  // Hasura allowlist can reject some role-scoped requests.
  // Retry once without x-hasura-role so token default role is used.
  if (isAllowlistError) {
    const context: any = operation.getContext() || {};
    if (!context.__retryWithoutRoleHeader) {
      return new Observable(observer => {
        const prevHeaders = context.headers || {};
        const nextHeaders = { ...prevHeaders };
        delete nextHeaders["x-hasura-role"];

        operation.setContext({
          ...context,
          __retryWithoutRoleHeader: true,
          headers: nextHeaders,
        });

        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      });
    }
  }

  if (isJwtError) {
    return new Observable(observer => {
      getNewAccessToken()
        .then((session: any) => {
          const idToken = session?.tokens?.idToken?.toString()
            || getStoredIdToken();
          const newHeaders: any = _buildAuthHeader(idToken)?.headers || {};
          operation.setContext(({ headers = {} }: any) => ({
            headers: { ...headers, ...newHeaders },
          }));
        })
        .then(() => {
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));
    });
  }
});

const setAuthorizationLink = setContext(
  async (request: any, previousContext: any) => {
    let idToken: any = getStoredIdToken();
    if (idToken) {
      const isExpired = isStoredTokenExpired(idToken);
      const isIssuedInFuture = isStoredTokenIssuedInFuture(idToken);
      if (isExpired || isIssuedInFuture) {
        const session: any = await getNewAccessToken();
        idToken = session?.tokens?.idToken?.toString()
          || getStoredIdToken();
      }
    }
    return _buildAuthHeader(idToken);
  }
);


const ADMIN_PORTAL_ROLES = [roles.ADMIN, roles.AFFILIATER_ADMIN, roles.SUB_ADMIN, roles.SUPER_USER, 'logitix_user', 'marketing_user'];

const _buildAuthHeader = (token: string) => {
  const hasuraRole = localStorage.getItem('x-hasura-user-role');
  console.log("Setting auth header with role:", hasuraRole);
  const headers: any = {};
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // Send x-hasura-role for admin portal roles so Hasura uses the correct role (e.g. affiliater_admin).
  // Without this, Hasura uses the JWT role (often "user"/"affiliater"), so search and other queries return no data.
  const claims = token ? getHasuraClaimsFromToken(token) : null;
  const allowedRoles: string[] = claims?.["x-hasura-allowed-roles"] || [];
  console.log("Allowed roles:", allowedRoles);
  console.log("Checking if hasuraRole is allowed:", hasuraRole);
  if (hasuraRole && ADMIN_PORTAL_ROLES.includes(hasuraRole) && allowedRoles.includes(hasuraRole)) {
    headers["x-hasura-role"] = hasuraRole;
  } else if (
    hasuraRole &&
    ADMIN_PORTAL_ROLES.includes(hasuraRole) &&
    !allowedRoles.includes(hasuraRole)
  ) {
    console.warn(
      `[Auth] Stored role "${hasuraRole}" is not in JWT x-hasura-allowed-roles (${allowedRoles.join(", ")}). ` +
        `Hasura will use the JWT default role instead — sub-admins need Cognito (Pre Token Generation) to emit ` +
        `"sub_admin" in https://hasura.io/jwt/claims, same pattern as the admin group.`
    );
  }

  return { headers };
};


const httpLink = new HttpLink({ uri: config.backend_url });
const client = new ApolloClient({
  link: from([errorLink, setAuthorizationLink, httpLink]),
  cache: new InMemoryCache({
    addTypename: false
  }),
});

root.render(
  <>
  <Helmet>
  <meta name="robots" content="noindex"></meta>
  </Helmet>
  <BrowserRouter>
  <ApolloProvider client={client}>
    <AppProvider>
    <App />
    </AppProvider>
  </ApolloProvider>
  </BrowserRouter>
  </>
);
serviceWorkerRegistration.register();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();









// // index.ts i frontend

// import React from "react";
// import { createRoot } from "react-dom/client";
// import App from "./App";
// import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from, Observable } from '@apollo/client'
// import {setContext} from '@apollo/client/link/context'
// import { onError } from '@apollo/client/link/error'
// import reportWebVitals from "./reportWebVitals";
// import { BrowserRouter, matchRoutes } from "react-router-dom";
// import { initializeFaro, createReactRouterV6DataOptions, ReactIntegration, getWebInstrumentations } from '@grafana/faro-react';
// import { TracingInstrumentation } from '@grafana/faro-web-tracing';
// import "./style/global.css";
// import {config} from './config/config'
// import {Amplify} from 'aws-amplify'
// import {fetchAuthSession} from 'aws-amplify/auth'
// import AppProvider from "./Provider/AppProvider";
// import * as serviceWorkerRegistration from "./serviceWorkerRegistration"
// import Helmet from "react-helmet";
// import { roles } from "./helper";
// import "@fontsource/poppins";
// import {
//   getHasuraClaimsFromToken,
//   getStoredIdToken,
//   isStoredTokenExpired,
//   isStoredTokenIssuedInFuture,
// } from "./authToken";
 
 
// Amplify.configure(config.amplifyConfig);
 
// const faroCollectorUrl = (process.env.REACT_APP_FARO_URL || "").trim();
// if (faroCollectorUrl) {
//   initializeFaro({
//     url: faroCollectorUrl,
//     app: {
//       name: process.env.REACT_APP_FARO_APP_NAME,
//       version: process.env.REACT_APP_FARO_APP_VERSION,
//       environment: process.env.REACT_APP_FARO_ENV,
//     },
//     instrumentations: [
//       ...getWebInstrumentations(),
//       new TracingInstrumentation(),
//       new ReactIntegration({
//         router: createReactRouterV6DataOptions({ matchRoutes }),
//       }),
//     ],
//   });
// }
 
// const container = document.getElementById("root");
// const root = createRoot(container!);
 
// const getNewAccessToken = async () => {
//   return fetchAuthSession({forceRefresh: true})
// };
 
// const errorLink = onError(({ graphQLErrors, operation, forward }) => {
//   const isJwtError = graphQLErrors?.some(
//     e => e.message?.includes('JWTExpired') || e.message?.includes('JWTIssuedAtFuture')
//   );
//   const isAllowlistError = graphQLErrors?.some(
//     (e) =>
//       e.message?.toLowerCase().includes("query is not allowed") ||
//       e.message?.toLowerCase().includes("allowlist")
//   );
 
//   // Hasura allowlist can reject some role-scoped requests.
//   // Retry once without x-hasura-role so token default role is used.
//   if (isAllowlistError) {
//     const context: any = operation.getContext() || {};
//     if (!context.__retryWithoutRoleHeader) {
//       return new Observable(observer => {
//         const prevHeaders = context.headers || {};
//         const nextHeaders = { ...prevHeaders };
//         delete nextHeaders["x-hasura-role"];
 
//         operation.setContext({
//           ...context,
//           __retryWithoutRoleHeader: true,
//           headers: nextHeaders,
//         });
 
//         forward(operation).subscribe({
//           next: observer.next.bind(observer),
//           error: observer.error.bind(observer),
//           complete: observer.complete.bind(observer),
//         });
//       });
//     }
//   }
 
//   if (isJwtError) {
//     return new Observable(observer => {
//       getNewAccessToken()
//         .then((session: any) => {
//           const idToken = session?.tokens?.idToken?.toString()
//             || getStoredIdToken();
//           const newHeaders: any = _buildAuthHeader(idToken)?.headers || {};
//           operation.setContext(({ headers = {} }: any) => ({
//             headers: { ...headers, ...newHeaders },
//           }));
//         })
//         .then(() => {
//           forward(operation).subscribe({
//             next: observer.next.bind(observer),
//             error: observer.error.bind(observer),
//             complete: observer.complete.bind(observer),
//           });
//         })
//         .catch(observer.error.bind(observer));
//     });
//   }
// });
 
// const setAuthorizationLink = setContext(
//   async (request: any, previousContext: any) => {
//     let idToken: any = getStoredIdToken();
//     if (idToken) {
//       const isExpired = isStoredTokenExpired(idToken);
//       const isIssuedInFuture = isStoredTokenIssuedInFuture(idToken);
//       if (isExpired || isIssuedInFuture) {
//         const session: any = await getNewAccessToken();
//         idToken = session?.tokens?.idToken?.toString()
//           || getStoredIdToken();
//       }
//     }
//     return _buildAuthHeader(idToken);
//   }
// );
 
 
// const ADMIN_PORTAL_ROLES = [roles.ADMIN, roles.AFFILIATER_ADMIN, roles.SUB_ADMIN, roles.SUPER_USER, 'logitix_user', 'marketing_user'];
 
// const _buildAuthHeader = (token: string) => {
//   const hasuraRole = localStorage.getItem('x-hasura-user-role');
//   console.log("Setting auth header with role:", hasuraRole);
//   const headers: any = {};
//   const isLocalMode = String(process.env.REACT_APP_LOCAL || "").toLowerCase() === "true";
//   const hasuraAdminSecret = process.env.REACT_APP_HASURA_ADMIN_SECRET;
 
//   if (token) {
//     headers.Authorization = `Bearer ${token}`;
//   } else if (isLocalMode && hasuraAdminSecret) {
//     // Pre-login role lookup runs without JWT; use admin secret only in local mode.
//     headers["x-hasura-admin-secret"] = hasuraAdminSecret;
//   }
 
//   // Send x-hasura-role for admin portal roles so Hasura uses the correct role (e.g. affiliater_admin).
//   // Without this, Hasura uses the JWT role (often "user"/"affiliater"), so search and other queries return no data.
//   const claims = token ? getHasuraClaimsFromToken(token) : null;
//   const allowedRoles: string[] = claims?.["x-hasura-allowed-roles"] || [];
//   console.log("Allowed roles:", allowedRoles);
//   console.log("Checking if hasuraRole is allowed:", hasuraRole);
//   if (hasuraRole && ADMIN_PORTAL_ROLES.includes(hasuraRole) && allowedRoles.includes(hasuraRole)) {
//     headers["x-hasura-role"] = hasuraRole;
//   } else if (
//     hasuraRole &&
//     ADMIN_PORTAL_ROLES.includes(hasuraRole) &&
//     !allowedRoles.includes(hasuraRole)
//   ) {
//     console.warn(
//       `[Auth] Stored role "${hasuraRole}" is not in JWT x-hasura-allowed-roles (${allowedRoles.join(", ")}). ` +
//         `Hasura will use the JWT default role instead — sub-admins need Cognito (Pre Token Generation) to emit ` +
//         `"sub_admin" in https://hasura.io/jwt/claims, same pattern as the admin group.`
//     );
//   }
 
//   return { headers };
// };
 
 
// const httpLink = new HttpLink({ uri: config.backend_url });
// const client = new ApolloClient({
//   link: from([errorLink, setAuthorizationLink, httpLink]),
//   cache: new InMemoryCache({
//     addTypename: false
//   }),
// });
 
// root.render(
//   <>
//   <Helmet>
//   <meta name="robots" content="noindex"></meta>
//   </Helmet>
//   <BrowserRouter>
//   <ApolloProvider client={client}>
//     <AppProvider>
//     <App />
//     </AppProvider>
//   </ApolloProvider>
//   </BrowserRouter>
//   </>
// );
// serviceWorkerRegistration.register();
// // If you want to start measuring performance in your app, pass a function
// // to log results (for example: reportWebVitals(console.log))
// // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();